import React, { useContext, useEffect, useState } from 'react'
import { Row, Col } from 'react-bootstrap'
import { useParams } from 'react-router-dom';
import { MdInfoOutline } from "react-icons/md";
import UpcomingEvents from '../../components/sections/UpcomingEvents';
import Button from '../../components/Button';
import Faq from '../../components/rj-components/Faq';
import ContactMap from '../../components/ContactMap';
import { AffContext } from '../../contexts/AffiliateContext';
import trackingLink from '../../util/trackingLink';
import GlobalContext from '../../contexts/store';
import getURLParameter from '../../helpers/getURLParameter';
import { Get } from '../../api/server';
import TicketCart from '../../components/TicketCart';
import usdFormatter from '../../helpers/usdFormatter';
import ticketPriceMinMax from '../../helpers/ticketPriceMinMax';
import buildExternalTicketUrl from '../../helpers/externalTicketLink';
import moment from 'moment';
import SiteButton from '../../components/rj-components/SiteButton';
import { Link } from 'react-router-dom';
import SeatingTicketCart from '../../components/SeatingTicketCart';
import EventGallery from '../../components/events/EventGallery';
import SimpleEventCard from '../../components/cards/SimpleEventCard';

const eventCardImage = (event) =>
  event?.promo?.story ||
  event?.promo?.banner ||
  event?.promo?.post ||
  event?.image ||
  "";

const eventCardLocation = (event) =>
  event?.location ||
  [
    event?.organization?.city,
    event?.organization?.state,
  ]
    .filter(Boolean)
    .join(", ") ||
  event?.organization?.name ||
  "";

const stripHtml = (html) => {
  if (!html) return '';

  return html
    // First remove all HTML tags except <p> and <br>
    // .replace(/<(?!\/?p|\/)[^>]+>/g, '')
    // Clean up any style or class attributes
    .replace(/\s*style="[^"]*"/g, '')
    .replace(/\s*class="[^"]*"/g, '')
    // Replace HTML entities
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .trim();
};

function Event() {
  const { aff } = useContext(AffContext);
  const { slug } = useParams();
  const { company, organizations, events, upcomingEvents } = useContext(GlobalContext);
  const [show, setShow] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(null);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [orgsWithEvents, setOrgsWithEvents] = useState([]);
  const [orgEventsMap, setOrgEventsMap] = useState([]);
  const [pageData, setPageData] = useState({});
  const [ticketQuantities, setTicketQuantities] = useState({});
  const [displayTicketPrices, setDisplayTicketPrices] = useState({}); // minPrice, maxPrice
  const [affId, setAffId] = useState(null);
  const [eventLink, setEventLink] = useState('');
  const [didAff, setDidAff] = useState(false);
  const [showFullDetails, setShowFullDetails] = useState(false);
  const [orgEvents, setOrgEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [thumbnailPhotos, setThumbnailPhotos] = useState([]);
  const [seatingChartLoading, setSeatingChartLoading] = useState(false);

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);

    // Update page title with event name if available
    if (pageData?.name) {
      const originalTitle = document.title;
      document.title = `${pageData.name || company?.name}`;

      // Cleanup - restore original title when component unmounts
      return () => {
        document.title = originalTitle;
        // setPageData({});
        // setCurrentTicket({});
        // setDisplayTicketPrices({});
        // setTicketQuantities({});
      };
    }
  }, [pageData?.name]);

  useEffect(() => {
    setCurrentTicket(null);
    setShow(false);
    setThumbnailPhotos([]);
    async function loadEventBySlug() {
      if (!slug) return;

      try {

        ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        // Get the event for the current organization.... /// DONT CHANGE THIS!
        const orgEvents = events
          .filter(event => event?.organization?.slug === slug || event?.slug === slug)
          .sort((a, b) => new Date(a.start_date_local) - new Date(b.start_date_local));
        const eventInfo = orgEvents.find(event => {
          const eventDate = new Date(event.start_date_local);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const eventDay = new Date(eventDate);
          eventDay.setHours(0, 0, 0, 0);
          return eventDay >= today;
        });

        if (!orgEvents || !eventInfo?.slug) return;
        const response = await Get(`/events/getEvent/${eventInfo?.slug}`);
        // END OF DONT CHANGE THIS!
        ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

        if (response) {
          setPageData(response);
          if (response?.organization?.gallery?.slug && response?.organization?.gallery?.slug !== 'undefined' && response?.organization?.gallery?.slug !== 'null') {
            const gallery = await Get(`/galleries/byslug/${response?.organization?.gallery?.slug}`);
            setThumbnailPhotos(gallery?.images || []);
          }
          const link = getURLParameter('aff');
          const mainlink = response?.ticket_link;
          if (link) {
            setEventLink(mainlink + '?aff=' + link);
          } else {
            setEventLink('?aff=Website');
          }
        }
      } catch (err) {
        console.log('page error', err);
      }
    }
    loadEventBySlug();
  }, [slug, aff, events]);

  const openExternalTicketLink = (ticketLink) => {
    const url = buildExternalTicketUrl(ticketLink, getURLParameter('aff') || aff);
    if (!url) return false;

    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  };

  async function getData(slug) {
    setSeatingChartLoading(true);
    try {
      let response = await Get(`/events/getEvent/${slug}`);
      if (!response) return false;

      const link = getURLParameter('aff');
      const mainlink = response?.ticket_link;
      const externalTicketUrl = buildExternalTicketUrl(mainlink, link || aff);
      if (externalTicketUrl) {
        setEventLink(externalTicketUrl);
      } else {
        setEventLink('?aff=Website');
      }

      // A valid external ticket URL intentionally bypasses the seat map.
      // Placeholder values such as the API string "null" are not ticket links.
      if (externalTicketUrl) {
        window.open(externalTicketUrl, '_blank', 'noopener,noreferrer');
        return false;
      }

      const seatingChart = await Get(`/eventseating/event/${response?._id}/display`);
      if (seatingChart) {
        response.seatingChart = seatingChart;
      }
      setPageData(response);
      setCurrentTicket(response);
      setDisplayTicketPrices(ticketPriceMinMax(response?.ticketinventory || []));
      return true;
    } catch (err) {
      console.log('page error', err);
      return false;
    } finally {
      setSeatingChartLoading(false);
    }
  }

  useEffect(() => {
    // Filter organizations with upcoming events and add their events
    const orgEvents = (events || []).filter(event => event?.organization?._id === pageData?.organization?._id);
    setOrgEvents(orgEvents);
    // window.scrollTo(0, 0);
  }, [pageData, events, slug]);

  useEffect(() => {
    async function sendData() {
      try {
        if (aff && pageData?._id) {
          await trackingLink(pageData?._id, aff).then(async (res) => await setAffId(res?._id || null));
          setDidAff(true);
        }
      } catch (err) {
        console.log('page error', err);
      }
    }
    sendData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aff, pageData]);

  useEffect(() => {
    document.body.style.backgroundColor = '#000000';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  // Date/time click — valid external URLs open externally; otherwise load the seat map.
  const handleEventSelection = async (event) => {
    if (openExternalTicketLink(event?.ticket_link)) {
      return;
    }

    // Same showtime clicked again (e.g. after closing the map) — reopen without replacing loaded seating data
    if (currentTicket?._id === event._id) {
      setShow(true);
      return;
    }

    setDisplayTicketPrices(ticketPriceMinMax(event?.ticketinventory || []));
    const shouldOpenSeatMap = await getData(event?.slug);
    if (shouldOpenSeatMap) {
      setShow(true);
    }
  };

  const cartEvent = currentTicket;
  const isSelectedShowtime = (event) => currentTicket?._id === event._id;

  const heroImage = pageData?.promo?.banner || "";
  const eventVideoSrc = pageData?.promo_video || pageData?.media?.video || "";
  // Show the video box when there is a video or a banner-sized image
  const hasVideoBox = Boolean(eventVideoSrc || heroImage);
  const eventDetailsHtml = pageData?.details || "";
  const venueLine = [
    pageData?.organization?.address,
    pageData?.organization?.city,
    [pageData?.organization?.state, pageData?.organization?.zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const organizationName = pageData?.organization?.name || "";
  const ageLabel = pageData?.age || "";

  const dateButtonClass = (event) =>
    `cursor-pointer rounded-xl border-[0.5px] px-4 py-4 transition hover:border-brand-blue/50 hover:bg-white/[0.08] ${
      isSelectedShowtime(event)
        ? "border-brand-blue bg-brand-blue/25"
        : "border-white/10 bg-white/[0.04]"
    }`;

  const otherShows = (() => {
    return (upcomingEvents || [])
      .filter((event) => event.slug !== pageData?.slug)
      .slice(0, 3);
  })();

  const aboutSection = (
    <section className="mb-4">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-brand-blue-bright">
        Details
      </p>
      <h2 className="mb-4 text-2xl font-bold tracking-tight text-white">
        About the show
      </h2>
      <div className="prose-invert max-w-none text-[15px] leading-relaxed text-white/70 [&_p]:mb-3">
        {showFullDetails ? (
          <div
            dangerouslySetInnerHTML={{
              __html: stripHtml(eventDetailsHtml),
            }}
          />
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: stripHtml(
                eventDetailsHtml
                  ? eventDetailsHtml.slice(0, 400) + (eventDetailsHtml.length > 400 ? "..." : "")
                  : ""
              ),
            }}
          />
        )}
      </div>
      {eventDetailsHtml.length > 400 && (
        <button
          type="button"
          className="mt-3 text-sm font-semibold text-brand-blue-bright transition hover:text-white"
          onClick={() => setShowFullDetails(!showFullDetails)}
        >
          {showFullDetails ? "Read less" : "Read more"}
        </button>
      )}
    </section>
  );

  const upcomingShowsSection = (
    <section className="w-full max-w-xl py-8 md:py-10">
      <p className="mb-1.5 text-left text-[11px] font-semibold uppercase tracking-eyebrow text-brand-blue-bright">
        Also on the strip
      </p>
      <h2 className="mb-5 text-left text-xl font-bold tracking-tight text-white md:text-2xl">
        Upcoming shows
      </h2>
      {otherShows.length > 0 ? (
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {otherShows.map((event, index) => (
            <Link
              key={event._id || index}
              to={`/events/event/${event.slug}`}
              className="block min-w-0 no-underline text-inherit"
            >
              <SimpleEventCard
                compact
                image={eventCardImage(event)}
                title={event?.organization?.name || event?.name}
                location={eventCardLocation(event)}
              />
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-left text-sm text-white/45">
          No other upcoming shows right now.
        </p>
      )}
    </section>
  );

  const dateOptions = (
    orgEvents && orgEvents.length > 0 ? (
      orgEvents.map((event) => (
        <button
          key={event._id}
          type="button"
          onClick={() => handleEventSelection(event)}
          className={`flex min-h-[72px] w-full cursor-pointer items-center justify-between gap-3 rounded-xl border-[0.5px] px-4 py-3 text-left transition hover:border-brand-blue/50 hover:bg-white/[0.08] ${
            isSelectedShowtime(event)
              ? "border-brand-blue bg-brand-blue/25"
              : "border-white/10 bg-white/[0.04]"
          }`}
        >
          <span className="text-base font-semibold leading-none text-white">
            {event.start_date_display}
          </span>
          <span className="inline-flex shrink-0 items-center rounded-md bg-brand-blue px-3 py-1.5 text-sm font-bold leading-none text-white">
            {event.start_time_display} {event?.timezone_abbr}
          </span>
        </button>
      ))
    ) : (
      <p className="text-sm text-white/40">No upcoming dates available</p>
    )
  );

  const mobileDateOptions = (
    orgEvents && orgEvents.length > 0 ? (
      orgEvents.map((event) => (
        <button
          key={event._id}
          type="button"
          onClick={() => handleEventSelection(event)}
          className={`flex min-h-[72px] w-full items-center py-3 text-left ${dateButtonClass(event)}`}
        >
          <div className="flex w-full items-center justify-between gap-3">
            <p className="mb-0 text-base font-semibold leading-none text-white">
              {event.start_date_display}
            </p>
            <p className="mb-0 inline-flex shrink-0 items-center rounded-md bg-brand-blue px-3 py-1.5 text-sm font-bold leading-none text-white">
              {event.start_time_display} {event?.timezone_abbr}
            </p>
          </div>
        </button>
      ))
    ) : (
      <p className="py-4 text-center text-sm text-white/40">
        No upcoming dates available
      </p>
    )
  );

  return (
    <div className="min-h-screen bg-ink font-sf-pro-display pt-[80px]">
      {/* —— Full-bleed event banner (promo.banner) —— */}
      <section className="relative isolate min-h-[52vh] w-full overflow-hidden md:min-h-[62vh]">
        <div className="absolute inset-0">
          {heroImage ? (
            <img
              src={heroImage}
              alt=""
              className="event-banner-media h-full w-full object-cover object-top"
            />
          ) : (
            <div
              className="h-full w-full"
              style={{
                background:
                  "radial-gradient(80% 60% at 50% 40%, rgba(0,174,239,0.22), transparent 55%), linear-gradient(180deg, #0A0D12 0%, #06080B 100%)",
              }}
            />
          )}
        </div>

        {/* Atmosphere overlays */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,10,10,0.25) 0%, rgba(10,10,10,0.45) 40%, rgba(10,10,10,0.92) 100%), radial-gradient(70% 55% at 75% 20%, rgba(0,174,239,0.2), transparent 55%)",
          }}
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ink to-transparent" />

        <div className="relative mx-auto flex min-h-[52vh] max-w-7xl flex-col justify-end px-4 pb-10 pt-20 sm:px-8 sm:pb-12 md:min-h-[62vh] md:px-10 lg:px-[40px]">
          <h1 className="max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tight text-white drop-shadow-sm sm:text-5xl md:text-6xl">
            {pageData?.name || "Loading show…"}
          </h1>
        </div>

        <style>{`
          @keyframes eventBannerDrift {
            from { transform: scale(1.05) translate3d(0, 0, 0); }
            to { transform: scale(1.12) translate3d(-1.2%, -0.8%, 0); }
          }
          .event-banner-media {
            animation: eventBannerDrift 28s ease-in-out infinite alternate;
            will-change: transform;
          }
        `}</style>
      </section>

      {/* —— Main —— */}
      <div className="relative mx-auto max-w-7xl px-4 py-8 sm:px-8 md:px-10 md:py-12 lg:px-[40px]">
        {/* Desktop: video + About | sticky Select date & time */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_440px] lg:items-stretch lg:gap-x-12">
          {/* Left column */}
          <div className="min-w-0 lg:col-start-1">
            {hasVideoBox ? (
              <>
                <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black ring-[0.5px] ring-white/10">
                  {eventVideoSrc ? (
                    <video
                      key={eventVideoSrc}
                      src={eventVideoSrc}
                      poster={heroImage || undefined}
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                      controlsList="nodownload"
                      preload="auto"
                      aria-label={pageData?.name ? `${pageData.name} event video` : "Event video"}
                      className="absolute inset-0 h-full w-full bg-black object-contain object-center"
                    />
                  ) : (
                    <img
                      src={heroImage}
                      alt=""
                      className="absolute inset-0 h-full w-full bg-black object-contain object-center"
                    />
                  )}
                </div>

                {/* Event name + venue under video, above details */}
                <div className="mt-5 lg:mt-6">
                  <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                    {pageData?.name || "Loading show…"}
                  </h2>
                  <div className="mt-3 flex flex-col gap-1.5 text-sm text-white/70">
                    {organizationName && (
                      <p className="font-semibold text-white/90">{organizationName}</p>
                    )}
                    {venueLine && <p>{venueLine}</p>}
                    {ageLabel && (
                      <p className="inline-flex items-center gap-1.5 text-white/80">
                        <MdInfoOutline size={15} />
                        {ageLabel}
                      </p>
                    )}
                  </div>
                </div>

                {/* Mobile select dates between video and about */}
                <div className="mt-6 mb-8 lg:hidden">
                  <p className="mb-2 text-[13px] font-semibold uppercase tracking-eyebrow text-brand-blue-bright">
                    Get tickets
                  </p>
                  <h2 className="mb-2 text-3xl font-extrabold tracking-tight text-white">
                    Select date
                  </h2>
                  <p className="mb-5 text-base text-white/60">
                    Tap a show date and time below to continue.
                  </p>
                  <div className="space-y-2 rounded-2xl border-2 border-brand-blue/30 bg-ink-700/80 p-4">
                    {mobileDateOptions}
                  </div>
                  {seatingChartLoading && (
                    <p className="mt-3 text-center text-sm font-semibold text-brand-blue-bright">
                      Opening seating chart…
                    </p>
                  )}
                </div>

                <div className="mt-6 lg:mt-8">{aboutSection}</div>

                <EventGallery photos={thumbnailPhotos} compact />

                {upcomingShowsSection}
              </>
            ) : (
              <>
                {aboutSection}
                <EventGallery photos={thumbnailPhotos} compact />
                {upcomingShowsSection}
              </>
            )}
          </div>

          {/* Right: sticky Select date & time — column stretches so sticky can track scroll */}
          <aside className="relative hidden lg:col-start-2 lg:block lg:self-stretch">
            <div className="sticky top-[96px] z-20 flex max-h-[calc(100vh-112px)] flex-col">
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border-2 border-brand-blue/35 bg-ink-700/95 shadow-[0_0_40px_rgba(0,174,239,0.12)]">
                <div
                  className="shrink-0 border-b border-white/10 px-5 pb-3 pt-4"
                  style={{
                    background:
                      "radial-gradient(120% 100% at 0% 0%, rgba(0,174,239,0.2), transparent 55%)",
                  }}
                >
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-eyebrow text-brand-blue-bright">
                    Tickets
                  </p>
                  <h2 className="text-2xl font-extrabold leading-tight text-white sm:text-[1.65rem]">
                    Select date &amp; time
                  </h2>
                  <p className="mt-1 text-sm text-white/60">
                    Tap a showtime below to open the seating chart.
                  </p>
                </div>

                <div className="flex min-h-0 flex-1 flex-col px-5 pb-4 pt-3">
                  <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
                    {dateOptions}
                  </div>
                  {seatingChartLoading && (
                    <p className="mt-3 shrink-0 text-center text-sm font-semibold text-brand-blue-bright">
                      Opening seating chart…
                    </p>
                  )}
                </div>
              </div>

            </div>
          </aside>

          {/* Mobile select dates when there is no video box (About is already above) */}
          {!hasVideoBox && (
            <div className="lg:col-span-2 lg:hidden">
              <div className="mb-4">
                <p className="mb-2 text-[13px] font-semibold uppercase tracking-eyebrow text-brand-blue-bright">
                  Get tickets
                </p>
                <h2 className="mb-2 text-3xl font-extrabold tracking-tight text-white">
                  Select date
                </h2>
                <p className="mb-5 text-base text-white/60">
                  Tap a show date and time below to continue.
                </p>
                <div className="space-y-2 rounded-2xl border-2 border-brand-blue/30 bg-ink-700/80 p-4">
                  {mobileDateOptions}
                </div>
                {seatingChartLoading && (
                  <p className="mt-3 text-center text-sm font-semibold text-brand-blue-bright">
                    Opening seating chart…
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Cart stays mounted for the seating modal; Find Tickets chrome is hidden */}
        {cartEvent && (
          <div className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0">
            <SeatingTicketCart
              show={show}
              event={cartEvent}
              handleClose={handleClose}
              handleShow={handleShow}
              currentTicket={cartEvent}
              company={company}
              ticketQuantities={ticketQuantities}
              setTicketQuantities={setTicketQuantities}
              affId={affId}
              ticketsLoading={seatingChartLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Event